import { GET, POST, DELETE, PATCH } from 'owa-ows-rest-api';
import type {
    NewOutlookSpace,
    NewOutlookSpaceAsset,
    OutlookSpace,
    OutlookSpaceAsset,
    OutlookSpaceAssetId,
    OutlookSpaceAssetSyncResult,
    OutlookSpaceId,
    OutlookSpacesFilter,
    OutlookSpacesService,
    OutputFromPreferHeader,
    PatchOutlookSpace,
    PatchOutlookSpaceAsset,
    SyncParameters,
} from 'owa-timestream-types';
import {
    spacesUri,
    spaceUri,
    assetsUri,
    assetUri,
    reorderChildrenUri,
    recycleBinUri,
    syncAssetsUri,
} from './OutlookSpacesOwsServiceUris';

/**
 * Client proxy for the Outlook Spaces Service implemented by OWS Prime.
 *
 * For documentation of this API, see
 * https://outlook-sdf.office.com/ows/swagger/index.html#/OutlookSpaces
 *
 * To access Spaces in the mailbox of the logged-in user, do not specify a target mailbox.
 * In this case, the URIs look like `GET /ows/api/spaces`.
 *
 * To access Spaces from a particular group, specify the target mailbox email address.
 * In this case, the URIs look like `GET /ows/spacesteam@service.microsoft.com/api/spaces`.
 */
export default class OutlookSpacesOwsClient implements OutlookSpacesService {
    private baseUrl: string;

    public constructor(public targetMailbox?: string | null) {
        this.baseUrl = 'ows/' + (!!targetMailbox ? targetMailbox + '/' : '');
    }

    getOutlookSpaces(spacesFilter?: OutlookSpacesFilter): Promise<OutlookSpace[]> {
        return GET<OutlookSpace[]>(
            'getOutlookSpaces',
            this.targetMailbox,
            this.baseUrl + spacesUri(spacesFilter)
        );
    }

    createOutlookSpace(newOutlookSpace: NewOutlookSpace): Promise<OutlookSpace> {
        return POST<OutlookSpace>(
            'createOutlookSpace',
            this.targetMailbox,
            this.baseUrl + spacesUri(),
            newOutlookSpace
        );
    }

    getOutlookSpace(outlookSpaceId: OutlookSpaceId): Promise<OutlookSpace> {
        return GET<OutlookSpace>(
            'getOutlookSpace',
            this.targetMailbox,
            this.baseUrl + spaceUri(outlookSpaceId)
        );
    }

    deleteOutlookSpace(outlookSpaceId: OutlookSpaceId): Promise<void> {
        return DELETE(
            'deleteOutlookSpace',
            this.targetMailbox,
            this.baseUrl + spaceUri(outlookSpaceId)
        );
    }

    patchOutlookSpace(
        outlookSpaceId: OutlookSpaceId,
        changes: PatchOutlookSpace
    ): Promise<OutlookSpace> {
        return PATCH<OutlookSpace>(
            'patchOutlookSpace',
            this.targetMailbox,
            this.baseUrl + spaceUri(outlookSpaceId),
            changes
        );
    }

    getOutlookSpaceAssets(outlookSpaceId: OutlookSpaceId): Promise<OutlookSpaceAsset[]> {
        return GET<OutlookSpaceAsset[]>(
            'getOutlookSpaceAssets',
            this.targetMailbox,
            this.baseUrl + assetsUri(outlookSpaceId)
        );
    }

    canSyncAssets() {
        return true;
    }

    syncAssets(
        outlookSpaceId: OutlookSpaceId,
        syncParameters: SyncParameters
    ): Promise<OutlookSpaceAssetSyncResult> {
        return GET<OutlookSpaceAssetSyncResult>(
            'syncAssets',
            this.targetMailbox,
            this.baseUrl + syncAssetsUri(outlookSpaceId, syncParameters)
        );
    }

    createOutlookSpaceAsset(
        outlookSpaceId: OutlookSpaceId,
        newAsset: NewOutlookSpaceAsset
    ): Promise<OutlookSpaceAsset> {
        return POST<OutlookSpaceAsset>(
            'createOutlookSpaceAsset',
            this.targetMailbox,
            this.baseUrl + assetsUri(outlookSpaceId),
            newAsset
        );
    }

    deleteOutlookSpaceAsset(
        outlookSpaceId: OutlookSpaceId,
        assetId: OutlookSpaceAssetId
    ): Promise<void> {
        return DELETE(
            'deleteOutlookSpaceAsset',
            this.targetMailbox,
            this.baseUrl + assetUri(outlookSpaceId, assetId) + '?deleteChildren=true'
        );
    }

    patchOutlookSpaceAsset(
        outlookSpaceId: OutlookSpaceId,
        assetId: OutlookSpaceAssetId,
        changes: PatchOutlookSpaceAsset,
        returnFullObject?: boolean | undefined
    ): Promise<OutputFromPreferHeader<OutlookSpaceAsset>> {
        return PATCH<OutputFromPreferHeader<OutlookSpaceAsset>>(
            'patchOutlookSpaceAsset',
            this.targetMailbox,
            this.baseUrl + assetUri(outlookSpaceId, assetId),
            changes,
            returnFullObject
        );
    }

    reorderOutlookSpaceAssetChildren(
        outlookSpaceId: OutlookSpaceId,
        assetId: OutlookSpaceAssetId
    ): Promise<OutlookSpaceAsset[]> {
        return POST<OutlookSpaceAsset[]>(
            'reorderOutlookSpaceAssetChildren',
            this.targetMailbox,
            this.baseUrl + reorderChildrenUri(outlookSpaceId, assetId),
            null
        );
    }

    emptyOutlookSpaceRecycleBin(outlookSpaceId: OutlookSpaceId): Promise<void> {
        return DELETE(
            'emptyOutlookSpaceRecycleBin',
            this.targetMailbox,
            this.baseUrl + recycleBinUri(outlookSpaceId)
        );
    }
}
